import PrimaryButton from "@/app/[locale]/components/buttons/PrimaryButton";
import React, { PropsWithChildren, useEffect, useState } from "react";
import dynamic from 'next/dynamic';
import Image from "next/image";
import { WAY, wayType } from "@/app/utils/payment";
import { toast } from "react-toastify";
import { PAYPAL_CLIENT_ID } from "@/app/env";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useLocale, useTranslations } from "next-intl";
import Loader from "@/app/[locale]/components/Feedbacks/Loader";
import { STRIPE_CLIENT_KEY } from "@/app/env";
import { StripeElementsOptions } from "@stripe/stripe-js";

// Dynamically import PayPal components
const PayPalButtons = dynamic(
  () => import('@paypal/react-paypal-js').then(mod => mod.PayPalButtons),
  { ssr: false }
);

const PayPalScriptProvider = dynamic(
  () => import('@paypal/react-paypal-js').then(mod => mod.PayPalScriptProvider),
  { ssr: false }
);

function PaymentCard({
  createOrder,
  disabled,
  paypalOncCanceled,
  orginalCost,
  handleChosePaypal,
  clientSecret,
  promoCode,
  paypalOnApprove,
  handleChosePayWithCard,
  forceReRender,
  values,
  price,
}: {
  orginalCost?: number;
  createOrder: any;
  values: any;
  promoCode: string | null;
  price: number;
  clientSecret: string;
  forceReRender: any[];
  handleChosePayWithCard: ({ onSuccess }: { onSuccess: () => void }) => void;
  handleChosePaypal: ({ onSuccess }: { onSuccess: () => void }) => void;
  paypalOnApprove: (orderID: string) => Promise<void>;
  paypalOncCanceled: (orderID: string) => Promise<void>;

  disabled: boolean;
}) {
  const [way, setWay] = useState<wayType | undefined>();
  const handelChangeWay = (way: wayType) => {
    if (way == WAY.CARD) {
      handleChosePayWithCard({
        onSuccess: () => setWay(way),
      });
    } else {
      handleChosePaypal({
        onSuccess: () => setWay(way),
      });
    }
  };
  useEffect(() => {
    if (way) {
      setWay(undefined);
    }
  }, [values, promoCode]);
  const t = useTranslations("contactUs.form");
  const [privacy, setPrivacy] = useState(false);
  const [personalData, setPersonalData] = useState(false);
  const isDisabled = disabled || !way || !privacy || !personalData;

  return (
    <div className="bg-white w-full lg:w-[43.75rem] relative z-10 shadow-md rounded-[25px] pt-[39px] pb-[39px] lg:pt-[5.62rem] lg:pb-[4rem] lg:px-[5rem] px-[20px]">
      <div className="flex mb-[4.87rem] lg:text-[1.5rem] text-[14px] items-center justify-between">
        <p className="font-medium"> {t("courseCost")}</p>
        <p className="font-bold">
          3 {t("months")} |{" "}
          <span className="relative">
            {price}€/{t("month")}
            {orginalCost && (
              <span className="absolute lg:text-[1.2rem] font-semibold text-[10px] start-0 top-[-20px] opacity-70 line-through">
                {orginalCost}€/{t("month")}
              </span>
            )}{" "}
          </span>
        </p>
      </div>
      <div className="flex flex-col mb-[2.75rem] gap-[16px] lg:gap-[1.87rem]">
        <PaymentOption
          isActive={way == "PAYPAL"}
          onClick={() => handelChangeWay(WAY.PAYPAL)}
        >
          {" "}
          <p>{t("payPal")} </p>
          <div className="lg:w-[9.06rem] flex justify-center items-center px-[14px] lg:px-[23px] h-[100%] bg-white rounded-[30px] w-[94px]">
            <div className="w-full h-full relative">
              <Image
                alt="PayPal logo"
                className="object-contain"
                src={"/images/pay_paypal.png"}
                fill
              />
            </div>
          </div>
        </PaymentOption>
        <PaymentOption
          isActive={way == "CARD"}
          onClick={() => handelChangeWay(WAY.CARD)}
        >
          <p>{t("creditCard")}</p>
          <div className="lg:w-[14.3rem] flex justify-center items-center px-[14px] lg:px-[23px] h-[100%] bg-white rounded-[30px] w-[116px]">
            <div className="w-full h-full relative">
              <Image
                alt="Credit card icons"
                className="object-contain"
                src={"/images/pay_card.png"}
                fill
              />
            </div>
          </div>
        </PaymentOption>
      </div>{" "}
      <div>
        {way == WAY.CARD && clientSecret ? (
          <StripeCheckOutProvider
            isDisabled={isDisabled}
            clientSecret={clientSecret}
            onChangePersonalData={setPersonalData}
            personalData={personalData}
            privacy={privacy}
            onChangePrivacy={setPrivacy}
          />
        ) : way == WAY.PAYPAL ? (
          <PayPalScriptProvider
            options={{ 
              clientId: PAYPAL_CLIENT_ID,
              currency: "EUR",
              intent: "capture"
            }}
          >
            <>
              <PrivacyPolicy
                onChangePersonalData={setPersonalData}
                personalData={personalData}
                privacy={privacy}
                onChangePrivacy={setPrivacy}
              />
              <div className={`relative ${isDisabled && " opacity-50"}`}>
                <PrimaryButton
                  type="button"
                  className="w-full flex-1 bg-orangeLight font-bold"
                >
                  {t("proceedPayment")}
                </PrimaryButton>
                {!isDisabled && (
                  <PayPalButtons
                    disabled={isDisabled}
                    className="!opacity-0 absolute !h-full inset-0 z-20"
                    fundingSource="paypal"
                    onError={(err) => {
                      toast.error(t("error"), {
                        position: "bottom-center",
                      });
                    }}
                    style={{ height: 55 }}
                    forceReRender={forceReRender}
                    onCancel={(data: any) => paypalOncCanceled(data.orderID)}
                    createOrder={createOrder}
                    onApprove={(data) => paypalOnApprove(data.orderID)}
                  />
                )}
              </div>
            </>
          </PayPalScriptProvider>
        ) : (
          <>
            {" "}
            <PrivacyPolicy
              onChangePersonalData={setPersonalData}
              personalData={personalData}
              privacy={privacy}
              onChangePrivacy={setPrivacy}
            />{" "}
            <div className={`relative  opacity-50`}>
              <PrimaryButton
                type="button"
                className="w-full flex-1 bg-orangeLight font-bold  "
              >
                {t("proceedPayment")}
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentCard;

const PaymentOption = ({
  children,
  isActive,
  onClick,
}: PropsWithChildren & {
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      onClick={onClick}
      className={`ps-[14px] transition-all duration-300  cursor-pointer flex items-center justify-between text-[14px] lg:text-[1.25rem] lg:ps-[1.87rem] border-secondary  w-full  lg:h-[3.12rem] h-[30px]  bg-[#FAFAFA] pe-[5px] lg:pe-[5px] py-[5px] lg:py-[5px] shadow-select border-[1px]  rounded-[30px] ${
        isActive && "bg-primary text-white font-bold !border-primary"
      }`}
    >
      {children}
    </div>
  );
};

const StripeCheckOut = ({
  isDisabled,
  ...props
}: {
  isDisabled: boolean;
  personalData: boolean;
  onChangePrivacy: (value: boolean) => void;
  onChangePersonalData: (value: boolean) => void;
  privacy: boolean;
}) => {
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);
  const elements = useElements();
  const handleSubmit = async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.

    setLoading(true);
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      setLoading(false);

      return;
    }

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${
          window.location.origin + window.location.pathname
        }/completion`,
      },
    });

    if (result.error) {
      toast.error(t("error"), {
        position: "bottom-center",
      });
      setLoading(false);
    } else {
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
    setLoading(false);
  };
  const t = useTranslations("contactUs.form");
  const [loaded, setLoded] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      setLoded(true);
    }, 2000);
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      {loading && <Loader />}

      <div className="lg:my-5 my-[20px]">
        <PaymentElement options={{ layout: "accordion" }} />
      </div>

      <PrivacyPolicy {...props} />
      <div className={`relative ${(isDisabled || !loaded) && " opacity-50"}`}>
        <PrimaryButton
          type="submit"
          disabled={isDisabled || !loaded}
          className="w-full flex-1 bg-orangeLight font-bold  "
        >
          {t("proceedPayment")}{" "}
        </PrimaryButton>
      </div>
    </form>
  );
};
const StripeCheckOutProvider = ({
  clientSecret,
  ...props
}: {
  clientSecret: string;
  isDisabled: boolean;
  personalData: boolean;
  onChangePrivacy: (value: boolean) => void;
  onChangePersonalData: (value: boolean) => void;
  privacy: boolean;
}) => {
  const locale = useLocale();
  const options: StripeElementsOptions = {
    // passing the client secret obtained from the server
    appearance: { variables: { colorPrimary: "#220859" } },
    clientSecret: clientSecret,
  };
  const stripePromise = loadStripe(STRIPE_CLIENT_KEY, {
    locale: locale as "ar" | "en",
  });

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripeCheckOut {...props} />
    </Elements>
  );
};
const PrivacyPolicy = ({
  personalData,
  privacy,
  onChangePrivacy,
  onChangePersonalData,
}: {
  personalData: boolean;
  onChangePrivacy: (value: boolean) => void;
  onChangePersonalData: (value: boolean) => void;
  privacy: boolean;
}) => {
  const t = useTranslations("contactUs.form");

  return (
    <>
      <div className="flex lg:mb-[1.25rem] mb-[11px] cursor-pointer  lg:gap-[13px] gap-[10px]">
        <input
          type="checkbox"
          className="w-[20px] h-[20px] focus:!border-[1px] focus:!border-solid  focus:!border-secondary !border-secondary !border-[1px] rounded-[5px]"
          id="processing"
          checked={personalData}
          onChange={(e) => {
            onChangePersonalData(e.target.checked);
          }}
          name="scales"
        />
        <label
          className="lg:text-[1.12rem] flex text-[14px]  font-medium"
          htmlFor="processing"
        >
          {t("personalData")}
        </label>
      </div>
      <div className="flex lg:mb-[4rem] mb-[35px] cursor-pointer  lg:gap-[13px] gap-[10px]">
        <input
          type="checkbox"
          className="w-[20px] h-[20px] focus:!border-[1px] focus:!border-solid  focus:!border-secondary !border-secondary !border-[1px] rounded-[5px]"
          id="privacy"
          name="scales"
          checked={privacy}
          onChange={(e) => {
            onChangePrivacy(e.target.checked);
          }}
        />
        <label
          className="lg:text-[1.12rem]   text-[14px]  font-medium"
          htmlFor="privacy"
        >
          <>
            {t("accept")}{" "}
            <a className="font-bold" target="_blank" href="/privacy-policy">
              {t("privacy")}
            </a>{" "}
            <a
              className="font-bold"
              target="_blank"
              href="/terms-and-conditions"
            >
              {t("terms")}{" "}
            </a>
            <a
              className="font-bold"
              target="_blank"
              href="/refund-policy"
            >
              {t("refund")}{" "}
            </a>
          </>
        </label>
      </div>
    </>
  );
};
